// NOTICE: KAPTION-INTEGRATION
// app-studio.UI.2-3 — helpers for embedding the Builder-hosted App Studio.

export const RELAUNCH = 'rumo-builder:relaunch';
export const LAUNCH = 'rumo-builder:launch';
// app-studio.INSTALL.6 — App Studio published, restored or deleted an app.
export const APPS_CHANGED = 'rumo-builder:apps-changed';
// app-studio.UI.4 — deep links. App Studio posts { type, path } when its
// screen changes (Rumo mirrors it in its URL); Rumo posts the same message
// back on browser back/forward. Both sides accept only the allowlist below.
export const NAVIGATE = 'rumo-builder:navigate';

// Mirrors rumo-builder packages/studio/web/src/route.ts. Builder app ids are
// lowercase letters and digits.
export const STUDIO_TABS = [
  'overview',
  'versions',
  'jobs',
  'data',
  'monitoring',
  'logs',
];
const APP_ID = /^[a-z0-9]{1,32}$/;
export const ROUTE_INDEX = 'kaption_app_studio_index';
export const ROUTE_APP = 'kaption_app_studio_app';
export const ROUTE_APP_TAB = 'kaption_app_studio_app_tab';
export const ROUTE_NAMES = [ROUTE_INDEX, ROUTE_APP, ROUTE_APP_TAB];

// `/`, `/apps/<id>`, `/apps/<id>/<tab>` → { appId, tab }; null otherwise.
export function parseStudioPath(path) {
  if (typeof path !== 'string' || path.length > 128) return null;
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return { appId: '', tab: 'overview' };
  if (parts[0] !== 'apps' || parts.length > 3) return null;
  if (!APP_ID.test(parts[1] || '')) return null;
  if (parts.length === 2) return { appId: parts[1], tab: 'overview' };
  return STUDIO_TABS.includes(parts[2])
    ? { appId: parts[1], tab: parts[2] }
    : null;
}

// The App Studio path for this Rumo route's params ('/' when they don't fit).
export function studioPathFromParams({ appId, tab } = {}) {
  if (typeof appId !== 'string' || !APP_ID.test(appId)) return '/';
  if (!tab || tab === 'overview') return `/apps/${appId}`;
  return STUDIO_TABS.includes(tab) ? `/apps/${appId}/${tab}` : '/';
}

// The Rumo route for an App Studio screen.
export function rumoRouteFor({ appId, tab }, accountId) {
  if (!appId) return { name: ROUTE_INDEX, params: { accountId } };
  if (tab === 'overview') {
    return { name: ROUTE_APP, params: { accountId, appId } };
  }
  return { name: ROUTE_APP_TAB, params: { accountId, appId, tab } };
}

export function studioOrigin(studioUrl) {
  try {
    return new URL(studioUrl).origin;
  } catch {
    return null;
  }
}

// <studio_url><path>?embedded=1#launch_token=<token> — the token travels in
// the fragment, so it never reaches a server log or a Referer header. The path
// (app-studio.UI.4) opens App Studio on that screen; only an allowlisted path
// is ever appended, anything else opens the app list.
export function studioFrameSrc(studioUrl, token, path = '/') {
  const base = String(studioUrl || '').replace(/\/+$/, '');
  const screen = parseStudioPath(path);
  const safePath = screen ? studioPathFromParams(screen) : '/';
  return `${base}${safePath}?embedded=1#launch_token=${encodeURIComponent(token)}`;
}

function parse(data) {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// The type of a message from App Studio; a message counts only from the
// studio origin AND from our iframe (null otherwise).
export function studioMessageType(event, { origin, source }) {
  if (!origin || event.origin !== origin) return null;
  if (!source || event.source !== source) return null;
  return parse(event.data)?.type || null;
}

// The screen App Studio says it is showing, or null (wrong origin/window,
// another type, or a path off the allowlist).
export function studioNavigation(event, expected) {
  if (studioMessageType(event, expected) !== NAVIGATE) return null;
  return parseStudioPath(parse(event.data)?.path);
}

export function isRelaunchRequest(event, expected) {
  return studioMessageType(event, expected) === RELAUNCH;
}

export function currentTheme() {
  return document.body.classList.contains('dark') ? 'dark' : 'light';
}
